import logger from 'shared/services/logger'
import { isDefined, isString } from 'shared/utils/type-guards'

type CommonSettings =
  | 'API_URI'
  | 'API_WS_URI'
  | 'APP_ENV'
  | 'HOST_HOSTNAME'
  | 'INTERCOM_SECRET_KEY'
  | 'K2_CLIENT_IP_SIGNING_SECRET'
  | 'K2_GRPC_GATEWAY'
  | 'K2_GRPC_WEB_GATEWAY'
  | 'LOCAL_K2_GRPC_GATEWAY'
  | 'LOCAL_K2_GRPC_WEB_GATEWAY'
  | 'LOCAL_K2_REROUTE'
  | 'NEXT_PUBLIC_WORKSPACE_API_URI'
  | 'NODE_ENV'
  | 'SESSION_SECRET_V2'
  | 'SESSION_SECRET'
  | 'SPLIT_IO_AUTHORIZATION_KEY'
  | 'SSR_SPLIT_IO_AUTHORIZATION_KEY'
  | 'VERCEL_ENV'
  | 'VERCEL_GIT_COMMIT_REF'
  | 'NEXT_PUBLIC_TENOR_API_KEY'
  | 'NEXT_PUBLIC_KLIPY_API_KEY'

export type APP_ENV =
  | 'local'
  | 'development'
  | 'staging'
  | 'production'
  | 'preview'

export type NODE_ENV = 'development' | 'test' | 'production'

interface Options {
  appName: string
  debug?: boolean
  required?: string[]
  publicEnvVars?: Record<string, string | undefined>
}

export class Settings<AppSettings extends string> {
  private appName: string
  private debug: boolean
  private required = ['API_URI', 'API_WS_URI', 'APP_ENV']

  private publicEnvVars: Record<string, string | undefined> = {
    K2_GRPC_WEB_GATEWAY: process.env.NEXT_PUBLIC_K2_GRPC_WEB_GATEWAY,
    LOCAL_K2_GRPC_WEB_GATEWAY:
      process.env.NEXT_PUBLIC_LOCAL_K2_GRPC_WEB_GATEWAY,
    LOCAL_K2_REROUTE: process.env.NEXT_PUBLIC_LOCAL_K2_REROUTE,
    API_URI: process.env.NEXT_PUBLIC_API_URI,
    API_WS_URI: process.env.NEXT_PUBLIC_API_WS_URI,
    APP_ENV: process.env.NEXT_PUBLIC_APP_ENV,
    HOST_HOSTNAME: process.env.NEXT_PUBLIC_HOST_HOSTNAME,
    NODE_ENV: process.env.NEXT_PUBLIC_NODE_ENV,
    SPLIT_IO_AUTHORIZATION_KEY:
      process.env.NEXT_PUBLIC_SPLIT_IO_AUTHORIZATION_KEY,
    NEXT_PUBLIC_WORKSPACE_API_URI: process.env.NEXT_PUBLIC_WORKSPACE_API_URI,
    NEXT_PUBLIC_TENOR_API_KEY: process.env.NEXT_PUBLIC_TENOR_API_KEY,
    NEXT_PUBLIC_KLIPY_API_KEY: process.env.NEXT_PUBLIC_KLIPY_API_KEY,
  }

  constructor(options: Options) {
    this.appName = options.appName
    this.debug = isDefined<boolean>(options.debug)
      ? options.debug
      : process.env.NEXT_PUBLIC_APP_ENV === 'local'

    this.publicEnvVars = { ...this.publicEnvVars, ...options.publicEnvVars }

    this.init()
    this.required = [...(options.required || []), ...this.required]
  }

  /**
   *
   * @param variable environment variable name
   * @param fallback default value to use if variable isn't found
   * @returns environment variable
   */
  getValue<ValueReturnType = string>(
    variable: CommonSettings | AppSettings,
    fallback?: string,
  ): ValueReturnType {
    const value =
      process.env[variable as string] ||
      this.publicEnvVars[variable] ||
      fallback

    return value as unknown as ValueReturnType
  }

  /**
   * The appEnv is a custom variable that matches the GUIDEcx deployment
   * and hosting environment
   */
  get appEnv(): APP_ENV {
    return (process.env.NEXT_PUBLIC_APP_ENV ||
      process.env.NEXT_PUBLIC_APP_ENV) as unknown as APP_ENV
  }

  /**
   * @deprecated we should not use a hard-coded hostname. The hostname should
   * be inferred from the request or window.location
   *
   * The hostname is based on the host application
   */
  get hostname(): string {
    if (typeof window !== 'undefined')
      return `https://${window.location.hostname}`

    return process.env.NEXT_PUBLIC_HOST_HOSTNAME || 'https://app.guidecx.com'
  }

  /**
   * Standard NODE_ENV variable to indicate if Node is in active
   * development or is running in hosting ("production") environment
   */
  get nodeEnv(): NODE_ENV {
    return this.getValue<NODE_ENV>('NODE_ENV', 'production')
  }

  private init() {
    this.validateRequired()

    // printing the required variables for debugging during startup
    if (this.debug) {
      logger.debug(`\n== '${this.appName}' REQUIRED APP SETTINGS ==`)
      this.required.forEach((key) => {
        logger.debug(`>> ${key}: ${this.getValue(key as AppSettings)}`)
      })
      logger.debug(`\n`)
    }
  }

  private validateRequired() {
    const missing = this.required.filter((key) => {
      if (!isString(key)) return false
      const value = this.getValue(key as CommonSettings)
      return isString(key) && !isString(value)
    })

    if (missing.length && ['local', 'development'].indexOf(this.appEnv) > -1) {
      throw new Error(`
        You're missing the following environment variables in ${this.appName}:
        
        ${missing.join(', ')}
        
        Please add them to the .env file in the monorepo root and confirm that the names 
        match any custom mapping that exists in the next.config.js publicRuntime configuration. 
      `)
    }
  }
}

const sharedSettings = new Settings({
  appName: 'shared',
})

export default sharedSettings
